import type {
  AssetId,
  BeatId,
  CaptionTokenId,
  CastSlotId,
  CastVariantId,
  EntityId,
  Milli,
  OpId,
  ScenarioId,
  Tick,
} from "./ids.js";

/** Current EditGraph / action-log schema. Deserialize migrates v1 → v2. */
export const EDIT_GRAPH_SCHEMA_VERSION = 2 as const;
/** Legacy wire/save version accepted by migrateEditGraphToCurrent. */
export const EDIT_GRAPH_SCHEMA_VERSION_V1 = 1 as const;
export type EditGraphSchemaVersion = typeof EDIT_GRAPH_SCHEMA_VERSION;

/**
 * @deprecated Compat alias from the fixed 2-slot SFX model. SFX are now an
 * id-keyed list (`EditOp.sfx.id` / `ResolvedEdit.sfx`); these two strings remain
 * valid instance ids so existing content/tests migrate without a hard break.
 * Do not gate new code on this set.
 */
export const SFX_SLOT_IDS = ["sfx-a", "sfx-b"] as const;
/** @deprecated SFX ids are free-form strings now. Use `string`. */
export type SfxSlotId = string;

/**
 * @deprecated Compat alias from the fixed 2-slot sticker model. Stickers are now
 * an id-keyed list (`EditOp.sticker.id` / `ResolvedEdit.stickers`); these two
 * strings remain valid sticker ids so existing content/tests migrate without a
 * hard break. Do not gate new code on this set.
 */
export const STICKER_SLOT_IDS = ["sticker-a", "sticker-b"] as const;
/** @deprecated Sticker ids are free-form strings now. Use `string`. */
export type StickerSlotId = string;

export const SFX_SLOT_ID_SET = new Set<string>(SFX_SLOT_IDS);
export const STICKER_SLOT_ID_SET = new Set<string>(STICKER_SLOT_IDS);

/** Normalized rectangle in authored camera space. Values are millipoints of full frame (0–1000). */
export interface NormalizedRect {
  xMilli: Milli;
  yMilli: Milli;
  wMilli: Milli;
  hMilli: Milli;
}

export type CutMode = "keep" | "omit" | "trim";

export type SoundRole = "bed" | "sting" | "ambience" | "silence";

/** Authored caption look — cosmetic presentation, not honesty labeling. */
export type CaptionAuthoredStyle = "clean" | "documentary" | "kinetic" | "urgent";

/** Deterministic caption token reveal animation. */
export type CaptionRevealMode = "static" | "word-by-word" | "pop";

/**
 * Semantic edit operations. Stable op IDs make undo/replay and serialization deterministic.
 * Timing uses integer ticks from the source pack tickRate — never wall-clock or FPS.
 */
export type EditOp =
  | {
      kind: "cut";
      opId: OpId;
      beatId: BeatId;
      mode: CutMode;
      /** Required when mode === "trim"; inclusive start in source ticks. */
      inTick?: Tick;
      /** Required when mode === "trim"; exclusive end in source ticks. */
      outTick?: Tick;
    }
  | {
      kind: "reorder";
      opId: OpId;
      beatOrder: BeatId[];
    }
  | {
      kind: "frame";
      opId: OpId;
      beatId: BeatId;
      subjectEntityId: EntityId | null;
      crop: NormalizedRect;
      zoomMilli: Milli;
    }
  | {
      kind: "text";
      opId: OpId;
      captionId: OpId;
      tokenIds: CaptionTokenId[];
      startTick: Tick;
      endTick: Tick;
      /**
       * Optional presentation — omitted fields use documentary/static/1000/center defaults.
       * Legacy ops without these fields remain valid.
       */
      style?: CaptionAuthoredStyle;
      revealMode?: CaptionRevealMode;
      /** Caption scale in millipoints (500–2000); default 1000. */
      scaleMilli?: Milli;
      /** Horizontal placement 0–1000; default 500 (center). */
      anchorXMilli?: Milli;
      /** Vertical placement 0–1000; default 820 (lower third). */
      anchorYMilli?: Milli;
      /** Must be a subset of tokenIds when present. */
      emphasizedTokenIds?: CaptionTokenId[];
    }
  | {
      kind: "sound";
      opId: OpId;
      assetId: AssetId;
      role: SoundRole;
      startTick: Tick;
      endTick: Tick;
      volumeMilli: Milli;
    }
  | {
      /**
       * Casting / Reconstruction: select an authored fictional variant for a cast slot.
       * Not a cosmetic skin picker — changes source provenance and delayed Cred risk.
       */
      kind: "cast";
      opId: OpId;
      slotId: CastSlotId;
      variantId: CastVariantId;
      /** Whether the required fictional/reconstruction disclosure is shown on the reel. */
      showReconstructionDisclosure: boolean;
    }
  | {
      /**
       * Id-keyed one-shot SFX (separate from the music/ambience `sound` lane).
       * `id` identifies the instance in the resolved list — last op per `id` wins
       * under the cursor. `assetId: null` clears that instance. A single `atTick`
       * fires the sound; it then plays its natural length (no end tick).
       */
      kind: "sfx";
      opId: OpId;
      id: string;
      assetId: AssetId | null;
      atTick: Tick;
      volumeMilli: Milli;
    }
  | {
      /**
       * Global look / grade treatment — last active op wins.
       * `treatmentId: null` clears (exact clear shape: no intensityMilli).
       */
      kind: "look";
      opId: OpId;
      treatmentId: string | null;
      /** Optional 0–1000 intensity when the catalog treatment supports it. Forbidden on clear. */
      intensityMilli?: Milli;
    }
  | {
      /**
       * Per-beat camera motion preset — last active op wins per beat.
       * `presetId: null` clears that beat's motion.
       */
      kind: "motion";
      opId: OpId;
      beatId: BeatId;
      presetId: string | null;
    }
  | {
      /**
       * Id-keyed timed sticker overlay. `id` identifies the sticker instance in the
       * resolved list — last op per `id` wins under the cursor.
       * `stickerId: null` clears that instance.
       */
      kind: "sticker";
      opId: OpId;
      id: string;
      stickerId: string | null;
      anchorXMilli: Milli;
      anchorYMilli: Milli;
      scaleMilli: Milli;
      /** Millidegrees (0–360000); 0 = upright. */
      rotationMilli: Milli;
      startTick: Tick;
      endTick: Tick;
    }
  | {
      /**
       * Transition at the outgoing boundary of `beatId` — last active op wins per beat.
       * `presetId: null` clears that boundary. Resolve also omits transitions on the
       * final retained beat after cuts/trims/reorders.
       */
      kind: "transition";
      opId: OpId;
      beatId: BeatId;
      presetId: string | null;
      durationTicks: Tick;
    };

/**
 * A resolved sticker overlay in the id-keyed list projection.
 * Paint order follows first-seen `id` order; cleared ids are omitted.
 * `stickerId` is always a concrete catalog id here (cleared instances are dropped).
 */
export interface StickerInstance {
  id: string;
  stickerId: string;
  anchorXMilli: number;
  anchorYMilli: number;
  scaleMilli: number;
  rotationMilli: number;
  startTick: number;
  endTick: number;
}

/** Resolved one-shot SFX instance (cleared instances are dropped). */
export interface SfxInstance {
  id: string;
  assetId: AssetId;
  atTick: number;
  volumeMilli: number;
}

/**
 * Append-only action log with a cursor.
 * Active ops are `ops.slice(0, cursor)`.
 * Undo/redo move the cursor; new ops truncate any redo branch.
 */
export interface EditGraph {
  schemaVersion: EditGraphSchemaVersion;
  sourcePackId: ScenarioId;
  sourcePackVersion: string;
  /** Must match SourcePack.tickRate for this project. */
  tickRate: number;
  /** Deterministic seed for scoring noise / challenge links. */
  seed: number;
  ops: EditOp[];
  cursor: number;
}

/** Resolved projection used by scoring and renderers. */
export interface ResolvedEdit {
  beatOrder: BeatId[];
  cuts: Map<BeatId, Extract<EditOp, { kind: "cut" }>>;
  frames: Map<BeatId, Extract<EditOp, { kind: "frame" }>>;
  captions: Extract<EditOp, { kind: "text" }>[];
  /**
   * Last active sound op wins (same pattern as frames/casts).
   * Length is 0 or 1. Undo moves the cursor so the previous sound becomes effective.
   * Music/ambience/silence lane only — keyed one-shots live in `sfx`.
   */
  sounds: Extract<EditOp, { kind: "sound" }>[];
  /** Last active cast op wins per slot (cursor-aware). */
  casts: Map<CastSlotId, Extract<EditOp, { kind: "cast" }>>;
  /**
   * Id-keyed one-shot SFX list. Last op per `id` wins; cleared ids
   * (`assetId: null`) are omitted. Ordered by first-seen `id` (fire order).
   */
  sfx: SfxInstance[];
  /** Last active non-cleared look op, or null when none / cleared under the cursor. */
  look: Extract<EditOp, { kind: "look" }> | null;
  /**
   * Last active non-cleared motion op per beat.
   * Cleared beats (`presetId: null`) are omitted.
   */
  motions: Map<BeatId, Extract<EditOp, { kind: "motion" }>>;
  /**
   * Id-keyed sticker list. Last op per `id` wins; cleared ids (`stickerId: null`)
   * are omitted. Ordered by first-seen `id` (paint order).
   */
  stickers: StickerInstance[];
  /**
   * Last active non-cleared transition op per outgoing beat boundary.
   * Cleared boundaries (`presetId: null`) are omitted.
   * Transitions on the final retained beat (after cuts/reorder) are also omitted.
   */
  transitions: Map<BeatId, Extract<EditOp, { kind: "transition" }>>;
}
